"use client";

import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface CollapsibleSectionProps {
  title: string;
  /** Start collapsed. Default false. */
  defaultCollapsed?: boolean;
  /** Optional count appended to the title, e.g. "Tags (12)". */
  count?: number;
  children: React.ReactNode;
  className?: string;
}

/**
 * Lightweight collapsible section with a chevron header. Shared by the inline
 * edit panel and the entity detail tabs so dense field groups can be folded
 * away — presentation only, the content is always one click from visible.
 */
export function CollapsibleSection({
  title,
  defaultCollapsed = false,
  count,
  children,
  className,
}: CollapsibleSectionProps) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  return (
    <div className={cn("border-t pt-3", className)}>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center justify-between text-left group"
        aria-expanded={!collapsed}
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
          {title}
          {typeof count === "number" ? ` (${count})` : ""}
        </span>
        {collapsed ? (
          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {!collapsed && <div className="mt-3">{children}</div>}
    </div>
  );
}
